<template>
  <div
    class="f-tag-field f-tag-field--lg"
  >
    <SelectWithSearch
      v-model="query"
      v-model:input-text="query"
      :name="`record.${fieldName}`"
      :placeholder="placeholder"
      :validator-name="validatorName"
      :required="isRequired"
      :search-helper="enumHelper"
      :record-type="recordType"
      :autocomplete="false"
      variant="hint-bar"
      @input="addTag"
      @keydown.exact="handleInput"
    >
      <template #before-input>
        <div
          v-show="modelValue.length > 0"
          class="f-tag-field__tags"
        >
          <div
            v-for="(option, idx) in modelValue"
            :key="option"
            :data-tag="option"
            :draggable="true"
            class="f-tag-field__tag"
          >
            <span>
              {{ getOptionName(option) }}
            </span>
            <button
              type="button"
              class="f-tag-field__tag-remove"
              :aria-label="$t('actions.remove')"
              @click="removeTag(idx)"
            />
          </div>
        </div>
      </template>
      <template #option="{option}">
        {{ $t(`fields.record.${fieldName}.values.${option}`) }}
      </template>
      <template #hint-bar>
        <div
          class="f-tag-field__hint"
          style="padding: 0;"
        >
          <span style="margin-right: 0.5rem;">
            {{ $t(`advancedsearch.fields.${fieldName}.hint`) }}
          </span>
          <svg
            width="25"
            height="18"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g
              fill="none"
              fill-rule="evenodd"
            >
              <rect
                stroke="var(--gray-400)"
                fill="var(--light-gray-300)"
                x=".5"
                y=".5"
                width="24"
                height="17"
                rx="2"
              />
              <g
                stroke="var(--gray)"
                stroke-linecap="round"
                stroke-width="1.2"
              >
                <path
                  stroke-linejoin="round"
                  d="M8 10.5h9v-4h-3"
                />
                <path d="M10 12.5l-2-2 2-2" />
              </g>
            </g>
          </svg>
        </div>
      </template>
    </SelectWithSearch>
  </div>
</template>

<script>
import SelectWithSearch, { SelectWithSearchHelper } from '#src/components/utils/fields/SelectWithSearch.vue'
import { MetadataUtils, fieldValues } from '@scholar/metadata-utils/main'
import { getI18n } from '#src/i18n'

export class EnumHelper extends SelectWithSearchHelper {
  constructor(options, fieldName, vm) {
    super(options)
    this.vm = vm
    this.fieldName = fieldName
  }

  filterOptions(query) {
    const filter = this.normalize(query)
    const parseOption = option => this.normalize(this.getOptionText(option))
    return this.options
      .filter(option => !this.vm.modelValue.includes(option))
      .filter(option => parseOption(option).startsWith(filter) || parseOption(option).includes(filter))
  }

  getOptionText(option) {
    const i18n = getI18n()
    return i18n.global.t(`fields.record.${this.fieldName}.values.${option}`)
  }
}

export default {
  components: {
    SelectWithSearch
  },
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    recordType: {
      type: String,
      required: true
    },
    fieldName: {
      type: String,
      required: true
    },
    validatorName: {
      type: String,
      default: null
    },
    isRequired: {
      type: Boolean,
      default: false
    },
    componentIdx: {
      type: Number,
      required: true
    }
  },
  emits: ['change'],
  data() {
    const options = { ...fieldValues, type: fieldValues.template }
    return {
      query: '',
      currentOptionIndex: 0,
      optionsAreVisible: false,
      validation: { status: true },
      enumHelper: new EnumHelper(options[this.validatorName], this.fieldName, this),
      parser: new MetadataUtils().getParserByType(this.recordType)
    }
  },
  computed: {
    placeholder() {
      if (this.$te(`advancedsearch.fields.${this.fieldName}.placeholder`)) {
        return this.$t(`advancedsearch.fields.${this.fieldName}.placeholder`)
      } else {
        return this.$t(`fields.record.${this.fieldName}.placeholder`)
      }
    }
  },
  watch: {
    modelValue() {
      this.$emit('change', this.modelValue)
    }
  },
  methods: {
    handleInput(e) {
      if (e.key === 'Backspace' && !this.filter) {
        this.removeTag(this.modelValue.length - 1)
      }
    },
    addTag(val) {
      this.modelValue.push(val)
      this.query = ''
      this.enumHelper.selected = ''
    },
    removeTag(idx) {
      this.modelValue.splice(idx, 1)
    },
    getOptionName(option) {
      return this.$t(`fields.record.${this.fieldName}.values.${option}`)
    }
  }
}
</script>
<style lang="scss" scoped>
@use "sass:color";
@use "#src/assets/scss/variables" as *;

.subject-field__hint {
  span + svg {
    margin-left: 0.5rem;
  }
}

.autocomplete-anchor {
  position: relative;
}

.autocomplete-list {
  box-sizing: content-box;
  position: absolute;
  width: 100%;
  max-height: 15rem;
  top: 0;
  left: -0.0625rem;
  z-index: 2;
  overflow-y: scroll;
  cursor: pointer;
  border: 0.0625rem solid #b5bbbf;
  box-shadow: 0 0.1875rem 0.4375rem 0 color.change(black, $alpha: 0.1);

  li {
    padding: 1.5rem;
    border-bottom: 0.0625rem solid rgb(69 85 95 / 40%);
    font-weight: 500;
    background: white;
    transition-property: background;
    @include md-transition;

    &:last-child {
      border-bottom: unset;
    }

    &[aria-selected="true"] {
      background: $light-gray-300;
    }
  }
}
</style>
<style lang="scss">
// has to be not scoped because the component uses v-html
.autocomplete-list li mark {
  font-weight: bold;
}
</style>
